<template>
	<div class="row maps">
		<div class="columns column3">
			<div class="highlights-navigation">
				<h3>{{ $t('highlightsHeader') }}</h3>
				<div v-for="(category, index) in items" :key="index" class="category">
					<div class="container highlight-category">
						<div
							class="highlight-item button button-compact"
							:class="{ active: activeCategory === parseInt(category.value, 10) }"
							@click="updateMarkers(parseInt(category.value, 10))"
						>
							<span>{{ category.name }}</span>
						</div>
						<slide-up-down
							class="highlight-content"
							:model-value="activeCategory === parseInt(category.value, 10)"
							:duration="300"
						>
							<ul>
								<li v-for="item in category.items" :key="item.ID">
									<a :data-id="`#huid_${item.ID}`" @click.prevent="openInfowindow($event)">
										{{ item.header }}
									</a>
								</li>
							</ul>
						</slide-up-down>
					</div>
				</div>
			</div>
		</div>
		<div class="columns column9">
			<div id="gmap" ref="gmap" />
		</div>
	</div>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import GoogleMaps from '@becurious/googlemaps';

const props = defineProps({
	items: { type: Array, default: () => [] },
});

const config = useRuntimeConfig();
const gmap = ref(null);
const activeCategory = ref(null);
const allHighlights = ref([]);

let map = null;
const mapData = {};

const mapConfig = {
	zoom: 15,
	center: { lat: 52.2448567, lng: 12.2453243 },

	styles: [{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] }],
	draggable: true,
	mapTypeControl: false,
	zoomControl: true,
	streetViewControl: true,
	scrollwheel: true,
};

watchEffect(() => {
	allHighlights.value = props.items.map((category) => category.items).flat();
});

const activeHighlights = computed(() =>
	allHighlights.value.filter((item) => {
		return parseInt(item.highlightsCategorie.ID, 10) === parseInt(activeCategory.value, 10);
	}),
);

onMounted(async () => {
	const google = await new Loader({
		apiKey: config.public.maps.apiKey,
	}).load();
	map = new GoogleMaps(google, gmap.value, mapConfig);
	initializeMap();
});

const initializeMap = () => {
	map.setMarkerIcon({
		src: '/images/marker.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.addInfoWindow(
		map.addMarker(Object.values(mapConfig.center), 'Hotel A2'),
		'<h3>Hotel A2</h3>' + '<p class="address">Schopsdorfer Heidestraße 10<br>39291 Schopsdorf</p>',
	);
};

const updateMarkers = (id) => {
	if (!map) {
		return;
	}

	if (id === activeCategory.value) {
		activeCategory.value = null;
	} else {
		activeCategory.value = id;
	}

	map.setMarkerIcon({
		src: '/images/marker-item.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.hideMarker('allcategory');

	activeHighlights.value.forEach((highlight) => {
		const marker = map.addMarker(
			[highlight.location.latitude, highlight.location.longitude],
			highlight.header,
			`cat_${highlight.category}`,
		);

		const infowindow = map.addInfoWindow(marker, getInfowindowContent(highlight));

		mapData[`#huid_${highlight.ID}`] = [infowindow, marker];
	});

	if (activeCategory.value) {
		map.autoCenter();
	}
};

const openInfowindow = (event) => {
	const ID = event.target.dataset.id;

	if (ID) {
		const currentMapData = mapData[event.target.dataset.id];
		map.openInfowindow(currentMapData[0], currentMapData[1]);
	}
};

const getInfowindowContent = (highlight) => {
	let html = '<h3>' + highlight.header + '</h3>';

	html += `<p class="address">
			${highlight.location.address}<br>${highlight.location.postalCode} ${highlight.location.city}
			</p>`;

	if (highlight.phonenumber) {
		html += `<p class="phone">${highlight.phonenumber}</p>`;
	}

	if (highlight.distance_public) {
		html += `<p class="directions public"><span class="icon-directions"></span>
				<span>${highlight.distancePublic}</span>
				</p>`;
	}

	if (highlight.distance_walk) {
		html += `<p class="directions walk"><span class="icon-directions"></span>
				<span>${highlight.distanceWalk}</span>
				</p>`;
	}

	if (highlight.url) {
		html += `<p class="website">
				<a href="${highlight.url}" target="_blank" rel="noopener noreferrer">Website</a>
				</p>`;
	}

	return html;
};
</script>

<style lang="scss" scoped>
#gmap {
	height: 600px;
}

.highlights {
	padding: 50px 0 0;
	width: 100%;

	.row {
		max-width: none;
		width: 100%;
		background: rgb(227 227 239 / 90%);
	}

	.column9 {
		padding: 0;
	}
}

.highlights-navigation {
	padding: 50px;

	h3 {
		margin-bottom: 20px;
	}

	a {
		cursor: pointer;
	}

	.button {
		width: 100%;
		border: 1px solid #ccc;
		border-radius: 0;
		background-color: $primary-background-color;
		margin-bottom: 10px;
		padding: 20px;
		font-weight: normal;

		&:hover,
		&.active {
			background: $primary-color;
			border-color: $primary-color;
			color: #fff;
		}
	}

	.button,
	li {
		font-size: $secondary-font-size;
	}
}

.highlight-item {
	width: 100%;
	color: #000;
	border: none;
	cursor: pointer;
	transition: all 0.4s linear;
	text-align: left;

	&.active,
	&:hover {
		color: #eb5757;
	}
}

.highlight-content {
	ul {
		margin: 0;
		padding-bottom: 20px;
		padding-left: 15px;
	}
}

.highlight-blocks {
	.row {
		padding: 25px 0;
	}

	.columns {
		padding: 20px;
	}

	span {
		margin: 0 10px 20px 0;
		display: inline-block;
	}
}

@media (max-width: 1500px) {
	.highlights-navigation {
		padding: 50px 25px;
	}
}

@media (max-width: 1280px) {
	.row.maps {
		width: 100%;

		.column3 {
			width: 40%;
		}

		.column9 {
			width: 60%;
		}
	}
}

@media (max-width: 960px) {
	.row.maps {
		width: 100%;

		.column3,
		.column9 {
			width: 100%;
		}
	}
}

@media (max-width: 600px) {
	.highlights .row {
		display: flex;
		flex-direction: column;
	}
}
</style>
